import React, { createContext, useState, useContext, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { AuthClient, LoginModel, UserDto } from '../../utils/apiClient';
import { SESSION_EXPIRED_KEY, getTokenExpiryMs, isTokenExpired } from '../../utils/authToken';

// Продление входа: токен живёт 24 часа (настраивается на сервере), а пока человек работает с сайтом,
// фронтенд заранее получает новый токен. Так судью не выбросит посреди турнира,
// а тот, кто не заходил сутки, войдёт заново.
const REFRESH_WHEN_LEFT_MS = 12 * 60 * 60 * 1000; // обновляем, когда до конца токена осталось меньше 12 часов
const CHECK_INTERVAL_MS = 5 * 60 * 1000;          // как часто проверяем срок токена
const ACTIVE_WINDOW_MS = 30 * 60 * 1000;          // «работает с сайтом» = действие за последние 30 минут
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

// Запросы, для которых 401 — штатный ответ (неверный пароль и т.п.), а не признак истёкшей сессии
const AUTH_URLS_WITHOUT_EXPIRY = ['/api/Auth/login', '/api/Auth/forgot-password', '/api/Auth/register'];

// Define the Auth Context type
interface AuthContextType {
  user: UserDto | undefined;
  loading: boolean;
  login: (email: string, password: string) => Promise<UserDto>;
  markPasswordChanged: () => void;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  isOwner: () => boolean;
}

// Роль владельца сайта: она не отдаётся в списке ролей и не назначается через интерфейс,
// а по правам покрывает все остальные роли — поэтому любая проверка роли для неё проходит.
const OWNER_ROLE = 'Owner';

// Create the Auth Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook for using the Auth Context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Auth Provider Component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDto>();
  const [loading, setLoading] = useState(true);

  const lastActivityRef = useRef(Date.now());
  const refreshingRef = useRef(false);

  // Load user from localStorage on initial render
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      if (parsed?.token && isTokenExpired(parsed.token)) {
        // Токен истёк, пока сайт был закрыт — просим войти заново
        localStorage.removeItem('user');
        sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
      } else {
        setUser(parsed);
        if (parsed?.token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${parsed.token}`;
        }
      }
    }
    setLoading(false);
  }, []);

  // Любой 401 от сервера для вошедшего пользователя = сессия истекла (или токен недействителен):
  // очищаем вход и отправляем на страницу входа. AuthProvider стоит выше Router, поэтому делаем полный переход.
  useEffect(() => {
    let redirecting = false;
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url: string = error?.config?.url ?? '';
        const isAuthEndpoint = AUTH_URLS_WITHOUT_EXPIRY.some((u) => url.includes(u));

        if (status === 401 && !isAuthEndpoint && localStorage.getItem('user') && !redirecting) {
          redirecting = true;
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
          window.location.assign('/login');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Скользящее продление: пока человек активен, заранее меняем токен на новый
  const token = user?.token;
  useEffect(() => {
    if (!token) return;

    const markActive = () => {
      lastActivityRef.current = Date.now();
    };

    const check = async () => {
      const stored = localStorage.getItem('user');
      const currentToken: string | undefined = stored ? JSON.parse(stored)?.token : undefined;
      if (!currentToken) return;

      const expiry = getTokenExpiryMs(currentToken);
      if (expiry === null) return;

      if (expiry <= Date.now()) {
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
        window.location.assign('/login');
        return;
      }

      const isActive = Date.now() - lastActivityRef.current < ACTIVE_WINDOW_MS;
      if (expiry - Date.now() > REFRESH_WHEN_LEFT_MS || !isActive || refreshingRef.current) return;

      refreshingRef.current = true;
      try {
        const response = await axios.post(`${ApiUrl}/api/Auth/refresh`);
        const refreshed = UserDto.fromJS(response.data);
        if (refreshed.token) {
          localStorage.setItem('user', JSON.stringify(refreshed));
          axios.defaults.headers.common['Authorization'] = `Bearer ${refreshed.token}`;
          setUser(refreshed);
        }
      } catch (error) {
        // 401 обработает перехватчик выше; при сетевой ошибке попробуем на следующей проверке
        console.warn('Не удалось продлить сессию:', error);
      } finally {
        refreshingRef.current = false;
      }
    };

    // Возвращение на вкладку после перерыва (в т.ч. после сна ноутбука) считаем действием и сразу проверяем токен
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        markActive();
        void check();
      }
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    void check();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [token]);

  // Login function
  const login = async (email: string, password: string): Promise<UserDto> => {
    const authClient = new AuthClient(ApiUrl, axios);
    try {
      setLoading(true);
      const response = await authClient.login(new LoginModel({ email, password }));
      const userData = response;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      lastActivityRef.current = Date.now();

      // Set token for future API calls
      axios.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // После успешной смены пароля сбрасываем флаг «пароль выдан системой»
  const markPasswordChanged = () => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = new UserDto({ ...prev, mustChangePassword: false });
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  // Register function
  const register = async (userData: any) => {
    const authClient = new AuthClient(ApiUrl, axios);
    try {
      setLoading(true);
      await authClient.register(userData);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setUser(undefined);
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const isOwner = () => {
    return Boolean(user && user.roles?.includes(OWNER_ROLE));
  };

  // Check if user has a specific role
  const hasRole = (role: string) => {
    return Boolean(user && (user.roles?.includes(role) || user.roles?.includes(OWNER_ROLE)));
  };

  // Check if user is an admin
  const isAdmin = () => {
    return hasRole('Админ');
  };

  const value = {
    user,
    loading,
    login,
    markPasswordChanged,
    register,
    logout,
    hasRole,
    isAdmin,
    isOwner
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}